import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Lock, Users, Monitor, ChevronRight, ArrowRight, BarChart3, FileCheck, Award, Building } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1758518729685-f88df7890776?auto=compress&cs=tinysrgb&w=1920';
const DSB_LOGO = '/images/edited-dsb-logo.png';

const T = {
  heroH1a: { de: 'Sicher.', en: 'Secure.' },
  heroH1b: { de: 'Digital.', en: 'Digital.' },
  heroH1c: { de: 'Zukunftsorientiert.', en: 'Future-oriented.' },
  heroSub: { de: 'Umfassende Beratungsdienstleistungen für den deutschen Mittelstand – von Datenschutz und IT-Sicherheit bis zur digitalen Transformation.', en: 'Comprehensive consulting services for German SMEs – from data protection and IT security to digital transformation.' },
  heroCta1: { de: 'Unsere Beratungsfelder', en: 'Our Consulting Areas' },
  heroCta2: { de: 'Beratung anfragen', en: 'Request Consultation' },
  certBadge: { de: 'Zertifizierte Beratung', en: 'Certified Consulting' },
  certSub: { de: 'Über 10 Jahre Erfahrung', en: 'Over 10 years experience' },
  quote: { de: 'Wir verstehen die Herausforderungen des Mittelstands und entwickeln praxisnahe Lösungen, die nachhaltig wirken.', en: 'We understand the challenges of SMEs and develop practical solutions that have a lasting impact.' },
  mgmtTitle: { de: 'Geschäftsführende Gesellschafter', en: 'Managing Partners' },
  aboutLabel: { de: 'Über uns', en: 'About Us' },
  aboutTitle: { de: 'Ihr Partner für nachhaltigen Unternehmenserfolg', en: 'Your Partner for Sustainable Business Success' },
  aboutP1: { de: 'DSB Business Solutions GmbH bietet umfassende Beratungsdienstleistungen für den deutschen Mittelstand. Unser Expertenteam unterstützt Sie in allen Bereichen der Unternehmensführung.', en: 'DSB Business Solutions GmbH offers comprehensive consulting services for German SMEs. Our expert team supports you in all areas of corporate management.' },
  aboutP2: { de: 'Als Teil der Basic United Holding GmbH vereinen wir Branchenexpertise mit einem ganzheitlichen Beratungsansatz. Wir stehen für praxisnahe, nachhaltige Lösungen.', en: 'As part of Basic United Holding GmbH, we combine industry expertise with a holistic consulting approach. We stand for practical, sustainable solutions.' },
  values: { de: ['Praxisnah', 'Ganzheitlich', 'Nachhaltig'], en: ['Practical', 'Holistic', 'Sustainable'] },
  servicesLabel: { de: 'Beratungsfelder', en: 'Consulting Areas' },
  servicesTitle: { de: 'Unsere Leistungen', en: 'Our Services' },
  servicesSub: { de: 'Ganzheitliche Beratung für alle Bereiche Ihrer Unternehmensführung', en: 'Holistic consulting for all areas of your corporate management' },
  allServices: { de: 'Alle Leistungen im Detail', en: 'All Services in Detail' },
  ctaTitle: { de: 'Beratungsgespräch vereinbaren', en: 'Schedule a Consultation' },
  ctaSub: { de: 'Lassen Sie uns gemeinsam Ihre Herausforderungen analysieren und Lösungen entwickeln.', en: 'Let us analyse your challenges together and develop solutions.' },
  ctaBtn: { de: 'Jetzt Termin anfragen', en: 'Request Appointment Now' },
};

const COMPETENCIES = {
  de: [{ num: '01', title: 'Datenschutz', desc: 'DSGVO-konforme Prozesse und Dokumentation' }, { num: '02', title: 'IT-Sicherheit', desc: 'Schutz vor Cyberbedrohungen' }, { num: '03', title: 'HR-Beratung', desc: 'Strategische Personalentwicklung' }, { num: '04', title: 'Digitalisierung', desc: 'Moderne IT-Infrastruktur' }],
  en: [{ num: '01', title: 'Data Protection', desc: 'GDPR-compliant processes and documentation' }, { num: '02', title: 'IT Security', desc: 'Protection against cyber threats' }, { num: '03', title: 'HR Consulting', desc: 'Strategic personnel development' }, { num: '04', title: 'Digitalisation', desc: 'Modern IT infrastructure' }],
};
const COMP_ICONS = [Shield, Lock, Users, Monitor];

const SERVICES_DATA = {
  de: [
    { title: 'Datenschutz & DSGVO', desc: 'Vollumfängliche Beratung und Umsetzung aller datenschutzrechtlichen Anforderungen für Ihr Unternehmen.' },
    { title: 'IT-Sicherheit', desc: 'Maßgeschneiderte Sicherheitskonzepte zum Schutz vor Cyberangriffen und Datenverlusten.' },
    { title: 'HR-Beratung', desc: 'Strategische Personalberatung für Talentgewinnung, -entwicklung und -bindung.' },
    { title: 'IT-Consulting', desc: 'Begleitung bei der Modernisierung Ihrer IT-Landschaft und Geschäftsprozesse.' },
    { title: 'Organisationsentwicklung', desc: 'Strukturen und Prozesse optimieren für nachhaltiges Wachstum.' },
    { title: 'Compliance', desc: 'Rechtssichere Unternehmensführung in allen regulatorischen Bereichen.' },
  ],
  en: [
    { title: 'Data Protection & GDPR', desc: 'Comprehensive consulting and implementation of all data protection requirements for your company.' },
    { title: 'IT Security', desc: 'Tailored security concepts to protect against cyber attacks and data loss.' },
    { title: 'HR Consulting', desc: 'Strategic HR consulting for talent acquisition, development and retention.' },
    { title: 'IT Consulting', desc: 'Support in modernising your IT landscape and business processes.' },
    { title: 'Organisational Development', desc: 'Optimising structures and processes for sustainable growth.' },
    { title: 'Compliance', desc: 'Legally secure corporate governance in all regulatory areas.' },
  ],
};
const SERVICE_ICONS = [Shield, Lock, Users, Monitor, BarChart3, FileCheck];

export default function DSBBusinessHome() {
  const { language } = useLanguage();
  const competencies = COMPETENCIES[language];
  const services = SERVICES_DATA[language];

  return (
    <main data-testid="sub-dsb-business-home" className="bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* ===================== HERO ===================== */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left - Content */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-8">
                <img src={DSB_LOGO} alt="DSB Business Solutions" className="h-12 w-auto" />
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F1A2E] mb-6 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {T.heroH1a[language]}<br />
                {T.heroH1b[language]}<br />
                <span className="text-[#F5A623]">{T.heroH1c[language]}</span>
              </h1>
              
              <p className="text-lg text-[#475569] leading-relaxed mb-10 max-w-lg">
                {T.heroSub[language]}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/dsb-business/leistungen" 
                  data-testid="hero-cta-leistungen"
                  className="group bg-[#0F1A2E] text-white px-8 py-4 text-sm font-bold tracking-wider uppercase inline-flex items-center gap-3 hover:bg-[#F5A623] transition-all duration-300"
                >
                  {T.heroCta1[language]}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/dsb-business/kontakt"
                  data-testid="hero-cta-kontakt" 
                  className="border border-[#0F1A2E]/20 text-[#0F1A2E] px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:border-[#0F1A2E] transition-all"
                >
                  {T.heroCta2[language]}
                </Link>
              </div>
            </motion.div>
            
            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#0F1A2E]/10">
                <img src={HERO_IMAGE} alt="Business Team Meeting" className="w-full h-[600px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2E]/20 to-transparent" />
              </div>
              
              {/* Floating badge */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-[#e2e8f0]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F5A623]/10 rounded-full flex items-center justify-center">
                    <Award size={24} className="text-[#F5A623]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0F1A2E]">{T.certBadge[language]}</p>
                    <p className="text-sm text-[#475569]">{T.certSub[language]}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== CORE COMPETENCIES ===================== */}
      <section className="py-20 border-y border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {competencies.map((comp, i) => {
              const Icon = COMP_ICONS[i];
              return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative pt-6 border-t border-[#0F1A2E]/10">
                <span className="absolute -top-4 left-0 text-5xl font-bold text-[#0F1A2E]/5" style={{ fontFamily: 'Outfit, sans-serif' }}>{comp.num}</span>
                <Icon size={24} className="text-[#F5A623] mb-4" />
                <h3 className="font-semibold text-[#0F1A2E] mb-2">{comp.title}</h3>
                <p className="text-sm text-[#475569]">{comp.desc}</p>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== ABOUT ===================== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left - Quote */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <span className="text-[200px] font-bold text-[#0F1A2E]/5 absolute -top-20 -left-8 leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>"</span>
                <blockquote className="relative z-10 text-2xl md:text-3xl font-medium text-[#0F1A2E] leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {T.quote[language]}
                </blockquote>
              </div>
              
              <div className="mt-10 pt-6 border-t border-[#e2e8f0]">
                <p className="font-semibold text-[#0F1A2E]">Admir Basic & Alessandro Costantino</p>
                <p className="text-sm text-[#475569]">{T.mgmtTitle[language]}</p>
              </div>
            </motion.div>
            
            {/* Right - Details */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">{T.aboutLabel[language]}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F1A2E] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {T.aboutTitle[language]}
              </h2>
              <p className="text-[#475569] leading-relaxed mb-6">{T.aboutP1[language]}</p>
              <p className="text-[#475569] leading-relaxed mb-8">{T.aboutP2[language]}</p>
              
              <div className="grid grid-cols-3 gap-6">
                {T.values[language].map((label, i) => (
                  <div key={i} className="text-center p-4 bg-[#f8fafc] rounded-lg">
                    <span className="text-2xl font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit, sans-serif' }}>0{i+1}</span>
                    <p className="text-sm text-[#0F1A2E] font-medium mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== SERVICES ===================== */}
      <section className="py-24 md:py-32 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">{T.servicesLabel[language]}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1A2E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.servicesTitle[language]}</h2>
            <p className="text-lg text-[#475569] max-w-2xl mx-auto">{T.servicesSub[language]}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="group bg-white p-8 rounded-xl border border-[#e2e8f0] hover:border-[#0F1A2E]/20 hover:shadow-xl hover:shadow-[#0F1A2E]/5 transition-all duration-300">
                <div className="w-14 h-14 bg-[#0F1A2E]/5 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#F5A623]/10 transition-colors"><Icon size={28} className="text-[#0F1A2E] group-hover:text-[#F5A623] transition-colors" /></div>
                <h3 className="text-xl font-semibold text-[#0F1A2E] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{service.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
              );
            })}
          </div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link 
              to="/dsb-business/leistungen"
              className="inline-flex items-center gap-2 bg-[#0F1A2E] text-white px-8 py-4 text-sm font-bold tracking-wider uppercase hover:bg-[#F5A623] transition-colors"
            >
              Alle Leistungen im Detail <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="py-24 md:py-32 bg-[#0F1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Building size={48} className="text-[#F5A623] mx-auto mb-6" />
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.ctaTitle[language]}</h2>
            <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">{T.ctaSub[language]}</p>
            <Link to="/dsb-business/kontakt" data-testid="cta-kontakt" className="inline-flex items-center gap-3 bg-white text-[#0F1A2E] px-10 py-5 text-sm font-bold tracking-wider uppercase hover:bg-[#F5A623] hover:text-white transition-all duration-300">
              {T.ctaBtn[language]} <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
